/** Сохранённые вкладки чата по языку интерфейса (bibleLanguage: ru, en, …), не по id перевода Библии. */

import { getStoredLanguageOrDefault } from './bibleLocaleSync';

export const MAX_CHAT_TABS = 5;
const STORAGE_KEY = 'verbum.chat.tabs.v1';

/** Ключ в объекте storage: код языка UI. */
export function getChatTabsStorageLanguageCode() {
  if (typeof window === 'undefined') return 'en';
  return getStoredLanguageOrDefault();
}

export function newSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function truncateTabText(s, maxChars = 40) {
  const t = String(s || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return '';
  return t.length > maxChars ? `${t.slice(0, maxChars - 1)}…` : t;
}

export function deriveSessionTitle(messages) {
  const first = messages?.find((m) => m.role === 'user' && !m.isFollowUp);
  if (!first?.text) return '';
  return truncateTabText(first.text, 40);
}

/** Заголовок вкладки в storage: для стиха — ссылка на стих; иначе — первый вопрос пользователя. */
export function computeTabStorageTitle(session, messages) {
  if (session?.verseReference?.trim()) {
    return truncateTabText(session.verseReference, 40);
  }
  return deriveSessionTitle(messages);
}

function getStoredStateForLanguage(languageCode) {
  const key = String(languageCode || 'en').toLowerCase();
  const all = safeParse(typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null);
  return all?.[key] ?? null;
}

/** Только то, что в localStorage для языка (или null). */
export function loadPersistedChatTabsState(languageCode) {
  const state = getStoredStateForLanguage(languageCode);
  if (!state?.sessions?.length) return null;
  return state;
}

/** @deprecated используйте loadPersistedChatTabsState */
export const loadPersistedTranslationState = loadPersistedChatTabsState;

function newEmptySessionRecord() {
  const id = newSessionId();
  return {
    id,
    title: '',
    messages: [],
    snapshot: null,
    updatedAt: Date.now(),
    verseReference: undefined,
    verseMessagePrefix: undefined,
  };
}

function createDefaultSingleTabState() {
  const s = newEmptySessionRecord();
  return { activeSessionId: s.id, sessions: [s] };
}

/** Есть ли хотя бы один отправленный вопрос пользователя (непустой текст). */
export function sessionHasPersistableUserQuestion(session) {
  const msgs = session?.messages;
  if (!Array.isArray(msgs) || msgs.length === 0) return false;
  return msgs.some((m) => m.role === 'user' && String(m.text || '').trim().length > 0);
}

/**
 * Вкладки без ни одного вопроса пользователя не пишем в storage (и отбрасываем при загрузке).
 * Если нечего сохранить — одна пустая сессия как при первом визите.
 */
export function getChatTabsStateForPersistence(state) {
  if (!state?.sessions?.length) return state;
  const kept = state.sessions.filter(sessionHasPersistableUserQuestion);
  if (kept.length === state.sessions.length) return state;

  if (kept.length === 0) {
    return createDefaultSingleTabState();
  }

  let activeSessionId = state.activeSessionId;
  if (!kept.some((s) => s.id === activeSessionId)) {
    const newest = [...kept].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))[0];
    activeSessionId = newest.id;
  }

  return {
    ...state,
    sessions: kept,
    activeSessionId,
  };
}

/** Восстановить вкладки из localStorage или одну пустую сессию (первый визит). Без автодобавления вкладки при каждом открытии модалки. */
export function loadChatTabsState(languageCode) {
  const state = getStoredStateForLanguage(languageCode);
  if (!state?.sessions?.length) {
    return createDefaultSingleTabState();
  }
  return getChatTabsStateForPersistence(state);
}

/** @deprecated используйте loadChatTabsState */
export const loadTranslationState = loadChatTabsState;

export function persistChatTabsState(languageCode, state) {
  if (typeof localStorage === 'undefined') return;
  const key = String(languageCode || 'en').toLowerCase();
  const all = safeParse(localStorage.getItem(STORAGE_KEY)) || {};
  all[key] = getChatTabsStateForPersistence(state);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (e) {
    console.warn('chatSessionsStorage: persist failed', e);
  }
}

/** @deprecated используйте persistChatTabsState */
export const persistTranslationState = persistChatTabsState;

export function updateActiveMessages(state, updater) {
  const id = state.activeSessionId;
  return updateSessionMessages(state, id, updater);
}

/** Обновить сообщения конкретной вкладки (нужно после await, если пользователь переключил активную вкладку). */
export function updateSessionMessages(state, sessionId, updater) {
  if (!state || !sessionId) return state;
  if (!state.sessions.some((s) => s.id === sessionId)) return state;
  return {
    ...state,
    sessions: state.sessions.map((s) => {
      if (s.id !== sessionId) return s;
      const prevMsgs = s.messages || [];
      const nextMsgs = typeof updater === 'function' ? updater(prevMsgs) : updater;
      const title = computeTabStorageTitle(s, nextMsgs);
      return { ...s, messages: nextMsgs, title, updatedAt: Date.now() };
    }),
  };
}

export function updateActiveSnapshot(state, snapshot) {
  const id = state.activeSessionId;
  return updateSessionSnapshot(state, id, snapshot);
}

export function updateSessionSnapshot(state, sessionId, snapshot) {
  if (!state || !sessionId) return state;
  if (!state.sessions.some((s) => s.id === sessionId)) return state;
  return {
    ...state,
    sessions: state.sessions.map((s) =>
      s.id === sessionId ? { ...s, snapshot, updatedAt: Date.now() } : s
    ),
  };
}

export function addNewSession(state) {
  let sessions = [...state.sessions];
  if (sessions.length >= MAX_CHAT_TABS) {
    const sorted = [...sessions].sort((a, b) => a.updatedAt - b.updatedAt);
    const dropId = sorted[0].id;
    sessions = sessions.filter((s) => s.id !== dropId);
  }
  const s = newEmptySessionRecord();
  sessions.push(s);
  return { activeSessionId: s.id, sessions };
}

export function closeSession(state, sessionId) {
  let sessions = state.sessions.filter((s) => s.id !== sessionId);
  if (sessions.length === 0) {
    const s = newEmptySessionRecord();
    return {
      activeSessionId: s.id,
      sessions: [s],
    };
  }
  let activeSessionId = state.activeSessionId;
  if (activeSessionId === sessionId) {
    activeSessionId = sessions[0].id;
  }
  return { activeSessionId, sessions };
}
